import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { signOut as firebaseSignOut, onIdTokenChanged } from "firebase/auth";
import { auth, sendEmailLink, signInWithGoogle } from "../lib/firebase";
import AuthModal from "../AuthModal";
import { saveMultipleRestaurants, upsertUser } from "../lib/api/user.apis";

export interface User {
  id: string;
  name: string;
  email: string;
  savedRestaurants: string[];
  restaurants: string[];
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  loading: boolean;
  setUser: (user: User | null) => void;
  signOut: () => Promise<void>;
  openAuthModal: () => void;
  closeAuthModal: () => void;
  signInWithGoogle: () => Promise<void>;
  signInWithEmailLink?: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  setUser: () => {},
  accessToken: null,
  loading: true,
  signOut: async () => {},
  openAuthModal: () => {},
  closeAuthModal: () => {},
  signInWithGoogle: async () => {},
  signInWithEmailLink: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const hasInitializedUser = useRef(false);

  // Listen to Firebase auth changes
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setAccessToken(null);
        setLoading(false);
        hasInitializedUser.current = false;
        return;
      }

      // 🚨 IMPORTANT: only set loading = true when user is NOT initialized yet
      if (!hasInitializedUser.current) {
        setLoading(true);
      }

      try {
        const token = await firebaseUser.getIdToken();

        setAccessToken(token);

        // 🚨 Only fetch backend ONCE
        if (!hasInitializedUser.current) {
          hasInitializedUser.current = true;

          const payload = {
            uid: firebaseUser.uid,
            name: firebaseUser.displayName || "Anonymous",
            email: firebaseUser.email || "",
            token,
          };

          const response = await upsertUser(payload);
          const serverUser = response.user;

          setUser({
            id: serverUser.uid,
            name: serverUser.name,
            email: serverUser.email,
            savedRestaurants: serverUser.savedRestaurants || [],
            restaurants: serverUser.restaurants || [],
          });

          // 🚨 loading becomes false ONLY after backend user is ready
          setLoading(false);
        }

        // 🚨 If initialized already → do NOT set loading=false
        // It keeps loading=false forever and prevents flicker

      } catch (err) {
        console.error("Auth error:", err);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Migrate guest likes after login
  const migrateGuestLikes = async () => {
    if (!user || !accessToken) return;

    const guestLikes: string[] = JSON.parse(localStorage.getItem("guestLikes") || "[]");
    if (guestLikes.length === 0) return;

    try {
      await saveMultipleRestaurants(guestLikes, accessToken);

      setUser(prev => ({
        ...prev!,
        savedRestaurants: [...new Set([...(prev?.savedRestaurants || []), ...guestLikes])]
      }));

      localStorage.removeItem("guestLikes");
    } catch (err) {
      console.error("Failed to migrate guest likes:", err);
    }
  };

  useEffect(() => {
    if (user && accessToken) migrateGuestLikes();
  }, [user, accessToken]);

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setAccessToken(null);
    } catch (error) {
      console.error("Sign-out failed:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        accessToken,
        loading,
        signOut,
        openAuthModal: () => setIsAuthModalOpen(true),
        closeAuthModal: () => setIsAuthModalOpen(false),
        signInWithGoogle: async () => {
          await signInWithGoogle();
          setIsAuthModalOpen(false);
        },
        signInWithEmailLink: async (email: string) => {
          await sendEmailLink(email);
          setIsAuthModalOpen(false);
        },
      }}
    >
      {children}
      <AuthModal isOpen={isAuthModalOpen} setIsOpen={() => setIsAuthModalOpen(false)} />
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
